import { lerp, angleLerp } from "@tank/shared";

export interface TankPos {
  x: number;
  z: number;
  rotY: number;
  turretAngle: number;
  alive: boolean;
  effects?: number; // effect bitmask (EFFECT_BIT) — drives ability visuals
}

interface Snapshot {
  t: number;
  states: Map<string, TankPos>;
}

/**
 * Ring buffer of recent state snapshots used to interpolate REMOTE tanks.
 * Render time is deliberately lagged (now - INTERP_DELAY) so we always have
 * two snapshots to interpolate between → smooth motion with no extrapolation.
 */
export class SnapshotBuffer {
  private snaps: Snapshot[] = [];
  private readonly cap = 30;

  capture(t: number, players: any): void {
    const states = new Map<string, TankPos>();
    players.forEach((p: any, id: string) => {
      states.set(id, {
        x: +p.x,
        z: +p.z,
        rotY: +p.rotY,
        turretAngle: +p.turretAngle,
        alive: !!p.alive,
        effects: +p.effects || 0,
      });
    });
    this.snaps.push({ t, states });
    if (this.snaps.length > this.cap) this.snaps.shift();
  }

  sample(renderTime: number): Map<string, TankPos> {
    const out = new Map<string, TankPos>();
    if (this.snaps.length === 0) return out;
    const first = this.snaps[0];
    const last = this.snaps[this.snaps.length - 1];
    if (renderTime <= first.t) return first.states;
    if (renderTime >= last.t) return last.states;

    let i = 0;
    while (i < this.snaps.length - 1 && this.snaps[i + 1].t < renderTime) i++;
    const s0 = this.snaps[i];
    const s1 = this.snaps[i + 1] ?? s0;
    const span = s1.t - s0.t || 1;
    const f = (renderTime - s0.t) / span;

    s1.states.forEach((p, id) => {
      const a = s0.states.get(id);
      if (!a) {
        out.set(id, p);
        return;
      }
      out.set(id, {
        x: lerp(a.x, p.x, f),
        z: lerp(a.z, p.z, f),
        rotY: angleLerp(a.rotY, p.rotY, f),
        turretAngle: angleLerp(a.turretAngle, p.turretAngle, f),
        alive: p.alive,
        effects: p.effects ?? 0,
      });
    });
    return out;
  }
}
